class TokenList {
  private currentPosition: number = -1;

  constructor(private readonly tokens: string[]) {
    this.currentPosition = (tokens.length > 0) ? 0 : -1;
  }

  public get position(): number {
    return this.currentPosition;
  }

  public get length(): number {
    return this.tokens.length;
  }

  public readToken(): string {
    const token = this.peekToken();
    this.currentPosition++;
    return token;
  }

  public peekToken(): string {
    return this.peekTokenAtPosition(this.currentPosition);
  }

  public peekNextToken(): string {
    return this.peekTokenAtPosition(this.currentPosition + 1);
  }

  private peekTokenAtPosition(position: number): string {
    if (position >= 0 && position < this.tokens.length) {
      return this.tokens[position];
    }
    return null;
  }
}

class WordTokeniser {
  private readonly tokenList: TokenList;

  constructor(private readonly messageText: string) {
    this.tokenList = this.getTokenList();
  }

  public toWords(): string[] {
    const words: string[] = [];
    let token = this.tokenList.readToken();
    while (token !== null) {
      if (this.isTokenSingleCharacter(token)) {
        while (this.isNextTokenSingleCharacter()) {
          token += this.tokenList.readToken();
        }
      }
      words.push(...this.trimPunctuationFromToken(token));

      token = this.tokenList.readToken();
    }
    return words;
  }

  private trimPunctuationFromToken(token: string): string[] {
    const punctuationInfo = this.getPunctuationInfoForToken(token);
    if (punctuationInfo && punctuationInfo.isPunctuated) {
      return [punctuationInfo.trimmedToken, punctuationInfo.token];
    }
    return [token];
  }

  private isNextTokenSingleCharacter(): boolean {
    const token = this.tokenList.peekToken();
    return this.isTokenSingleCharacter(token);
  }

  private isTokenSingleCharacter(token: string): boolean {
    return this.isSingleCharacter(token) || this.isSinglePunctuatedCharacter(token);
  }

  private isSinglePunctuatedCharacter(token: string): boolean {
    if (token) {
      const punctuationInfo = this.getPunctuationInfoForToken(token);
      if (punctuationInfo && punctuationInfo.isPunctuated) {
        return this.isSingleCharacter(punctuationInfo.trimmedToken);
      }
    }
    return false;
  }

  private isSingleCharacter(token: string): boolean {
    return token !== null && token.length === 1;
  }

  private getPunctuationInfoForToken(token: string) {
    const lhsPunctuationRegEx = /^[!"£$%\^&*\(\)-_=+`¬\\\|,<\.>\/\?\[\]\{\};:'@#~]+([^!"£$%\^&*\(\)-_=+`¬\\\|,<\.>\/\?\[\]\{\};:'@#~]+)/gm;
    const rhsPunctuationRegEx = /([^!"£$%\^&*\(\)-_=+`¬\\\|,<\.>\/\?\[\]\{\};:'@#~]+)[!"£$%\^&*\(\)-_=+`¬\\\|,<\.>\/\?\[\]\{\};:'@#~]+$/gm;
    const lhsMatches = lhsPunctuationRegEx.exec(token);
    const rhsMatches = rhsPunctuationRegEx.exec(token);
    const isLhsPunctuated = (lhsMatches && lhsMatches.length === 2);
    const isRhsPunctuated = (rhsMatches && rhsMatches.length === 2);
    const trimmedToken = isLhsPunctuated ? lhsMatches[1] : ( isRhsPunctuated ? rhsMatches[1] : token);
    return {
      token,
      trimmedToken,
      isPunctuated: isLhsPunctuated || isRhsPunctuated
    };
  }

  private getTokenList(): TokenList {
    return new TokenList(this.splitMessageIntoTokens());
  }

  private splitMessageIntoTokens(): string[] {
    let messageText = this.messageText.toLowerCase();
    messageText = this.trimMessageWhiteSpace(messageText);
    messageText = this.removeDoubleWhiteSpace(messageText);
    return messageText.split(/\s/gm);
  }

  private trimMessageWhiteSpace(messageText: string): string {
    return messageText.trim();
  }

  private removeDoubleWhiteSpace(messageText: string): string {
    return messageText.replace(/\s\s/gmi, ' ');
  }
}

export { WordTokeniser };




// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/components/chat/_domain/modules/alert-words/word-tokeniser.ts